import axios from 'axios';
import { rootInstance } from '../axiosConfig';

// This flow only uses the public router, including its CSRF proof and recovery.
const publicClient = axios.create({ baseURL: `${rootInstance.defaults.baseURL}api/public/cotizaciones/`, withCredentials: true });
let csrfPending: Promise<string> | null = null;
publicClient.interceptors.request.use(async config => {
    if (!['get', 'head', 'options'].includes((config.method || 'get').toLowerCase())) {
        if (!csrfPending) csrfPending = publicClient.get<{csrfToken: string}>('csrf/')
            .then(response => response.data.csrfToken).finally(() => { csrfPending = null; });
        config.headers.set('X-CSRFToken', await csrfPending);
    }
    return config;
});

export interface SucursalItem {
    id: number;
    nombre: string;
    direccion: string;
    codigo: string;
    telefono: string;
}

export interface CategoriaItem {
    id: number;
    nombre: string;
    descripcion: string;
}

export interface ServicioItem {
    id: number;
    nombre: string;
    descripcion: string;
    imagen: string | null;
    requiere_espacio: boolean;
    categoria_id: number;
    sucursales: number[];
}

export interface PrecioItem {
    id: number;
    servicio_id: number;
    plan_nombre: string;
    unidad: 'hora' | 'dia' | string;
    dias: number | null;
    horas: number | null;
    usa_temporizador: boolean;
    precio_inicial: number;
    precio_posterior: number | null;
    duracion_inicial: number | null;
}

export interface EspacioItem {
    id: number;
    espacio_id: number;
    espacio_nombre: string;
    espacio_descripcion: string;
    cantidad_personas: number;
    espacio_imagen: string | null;
    servicio_id: number;
    sucursal_id: number;
}

export interface CatalogoCotizacion {
    sucursales: SucursalItem[];
    categorias: CategoriaItem[];
    servicios: ServicioItem[];
    precios: PrecioItem[];
    espacios: EspacioItem[];
}

export interface CotizacionPayload {
    clave_solicitud?: string;
    sucursal_id: number;
    servicio_id: number;
    precio_plan_servicio_id: number;
    espacio_servicio_id?: number | null;
    fecha_inicio: string;
    fecha_fin?: string;
    hora_inicio?: string;
    hora_fin?: string | null;
    cantidad_periodos: number;
    dias_habiles?: boolean;
    cupon_codigo?: string;
    nombre: string;
    apellido: string;
    email: string;
    telefono: string;
    ci_nit?: string;
    empresa?: string;
    observaciones?: string;
    personas?: number;
}

export interface CotizacionResponse {
    recuperacion_token?: string;
    success: boolean;
    mensaje: string;
    cotizacion: {
        id: number;
        documento_token?: string;
        pdf_url?: string;
        creada_en?: string;
        observaciones?: string;
        condiciones_pago?: string[][];
        datos_empresa?: { direccion: string; telefono: string; email: string };
        detalle_pdf?: { hora_fin_estimada?: string; usa_temporizador?: boolean; unidad: string; dias?: number | null; horas?: number | null; precio_unitario: number; personas: number; capacidad?: number; descripcion_espacio?: string; plan_nombre?: string };
        codigo: string;
        codigo_reserva: string;
        solicitud_id: number;
        estado: string;
        expira_en: string;
        sucursal: string;
        servicio: string;
        espacio: string;
        fecha_inicio: string;
        fecha_fin: string;
        hora_inicio: string | null;
        hora_fin: string | null;
        cantidad_periodos: number;
        subtotal: number;
        descuento: number;
        total: number;
        cliente: {
            nombre: string;
            email: string;
            telefono: string;
            empresa: string;
        };
        whatsapp_url: string | null;
    };
}

export interface ReservaItem {
    dias_habiles?: boolean;
    id: number;
    fecha_inicio: string;
    fecha_fin: string;
    hora_inicio: string | null;
    hora_fin: string | null;
    estado: string;
    espacio_id?: number | null;
    espacio_nombre?: string | null;
}

export interface DisponibilidadResponse {
    hoy: string;
    reservas: ReservaItem[];
}

export const fetchCatalogoCotizacion = async (): Promise<CatalogoCotizacion> => {
    const response = await publicClient.get<CatalogoCotizacion>('catalogo/');
    return response.data;
};

export const fetchDisponibilidad = async (params: {
    espacio_id?: number | null;
    espacio_servicio_id?: number | null;
    sucursal_id?: number | null;
    servicio_id?: number | null;
}): Promise<DisponibilidadResponse> => {
    const response = await publicClient.get<DisponibilidadResponse>('disponibilidad/', { params });
    return response.data;
};

export const crearCotizacionPublica = async (payload: CotizacionPayload): Promise<CotizacionResponse> => {
    try {
        const response = await publicClient.post<CotizacionResponse>('crear_cotizacion/', payload);
        if (response.data.recuperacion_token) {
            await publicClient.post('recordar/', {token: response.data.recuperacion_token});
        }
        return response.data;
    } catch (error: any) {
        const msg = error.response?.data?.error || error.response?.data?.detail || Object.values(error.response?.data || {}).flat().join(' ') || error.message || 'Error al generar la cotización';
        throw new Error(msg);
    }
};


export const crearDocumentoCotizaciones = async (tokens: string[]): Promise<string> => {
    const response = await publicClient.post<{pdf_url: string}>('documento/', {tokens});
    return response.data.pdf_url;
};
